import React, { useState, useEffect, useMemo } from 'react';
import { Card, Button, Form, Row, Col, Alert, Container } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { InlineLoader } from '../../components/LoadingSpinner';
import { api } from '../../shared/api';
import Select from '../../components/form/AppSelect';
import AsyncCreatableSelect from '../../components/form/AppAsyncCreatableSelect';
import '../../shared/ReactSelectDarkMode.css';
import { useTranslation } from 'react-i18next'; // Import translation hook
import { useCurrency } from '../../shared/CurrencyContext';
import { useQuery, useMutation } from '@tanstack/react-query';

// Custom theme for React Select to support dark mode
const selectTheme = theme => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary: 'var(--tblr-primary)',
    primary75: 'var(--tblr-primary-rgb)',
    primary50: 'var(--tblr-primary-rgb)',
    primary25: 'var(--tblr-primary-rgb)',
    danger: 'var(--tblr-danger)',
    dangerLight: 'var(--tblr-danger-rgb)',
    neutral0: 'var(--tblr-bg-forms)',
    neutral5: 'var(--tblr-bg-surface)',
    neutral10: 'var(--tblr-bg-surface)',
    neutral20: 'var(--tblr-border-color)',
    neutral30: 'var(--tblr-border-color)',
    neutral40: 'var(--tblr-body-color-secondary)',
    neutral50: 'var(--tblr-body-color-secondary)',
    neutral60: 'var(--tblr-body-color-secondary)',
    neutral70: 'var(--tblr-body-color-secondary)',
    neutral80: 'var(--tblr-body-color)',
    neutral90: 'var(--tblr-body-color)'
  }
});

const ProjectCreate = () => {
  const { t } = useTranslation(); // Initialize translation hook
  const { currencies } = useCurrency();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [selectedCustomerOption, setSelectedCustomerOption] = useState(null);
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    customer: '',
    description: '',
    location: '',
    start_date: '',
    end_date: '',
    status: '',
    budget: '',
    currency: '',
    tax_rate: '21.00',
    notes: '',
    processed_by: null
  });

  const { data: initData } = useQuery({
    queryKey: ['project-create-init'],
    queryFn: async () => {
      const [statusesRes, crewRes, taxRes] = await Promise.all([
        api.get('projects/api/project-statuses/'),
        api.get('crew/api/crew/'),
        api.get('settings/api/tax/default/').catch(() => null),
      ]);
      const statuses = Array.isArray(statusesRes) ? statusesRes : (statusesRes.results || []);
      const members = Array.isArray(crewRes) ? crewRes : (crewRes.results || []);
      const taxRate = taxRes?.rate || taxRes?.data?.rate || null;
      return { statuses, members, taxRate };
    },
  });

  useEffect(() => {
    if (initData?.taxRate) {
      setFormData(prev => ({ ...prev, tax_rate: String(initData.taxRate) }));
    }
  }, [initData]);

  const projectStatuses = initData?.statuses || [];

  const crewMemberOptions = useMemo(
    () => (initData?.members || []).map(cm => ({
      value: cm.id,
      label: `${cm.company?.company_name || ''} | ${cm.display_name || ''}`
    })),
    [initData]
  );

  const currencyOptions = useMemo(
    () => (currencies || []).map(curr => ({ value: curr.id, label: `${curr.code} - ${curr.name}` })),
    [currencies]
  );

  const loadCustomerOptions = async (inputValue) => {
    try {
      const query = inputValue ? `?page_size=20&search=${encodeURIComponent(inputValue)}` : '?page_size=20';
      const response = await api.get(`customers/api/customers/${query}`);
      const results = Array.isArray(response) ? response : (response.results || []);
      return results.map(c => ({
        value: c.id,
        label: `${c.company_name || ''}${c.resp_person ? ' | ' + c.resp_person : ''}`
      }));
    } catch (err) {
      console.error('Error searching customers:', err);
      return [];
    }
  };

  const handleCreateCustomer = async (inputValue) => {
    try {
      setCreatingCustomer(true);
      const created = await api.post('customers/api/customers/', { company_name: inputValue.trim() });
      const newOption = { value: created.id, label: created.company_name };
      setSelectedCustomerOption(newOption);
      setFormData(prev => ({ ...prev, customer: created.id }));
    } catch (err) {
      console.error('Error creating customer:', err);
    } finally {
      setCreatingCustomer(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSelectChange = (name, selectedOption) => {
    setFormData({
      ...formData,
      [name]: selectedOption ? selectedOption.value : ''
    });
  };

  const saveMutation = useMutation({
    mutationFn: () => api.post('projects/api/projects/', formData),
    onSuccess: (response) => {
      navigate(`/projects/detail/${response.id}`);
    },
    onError: (err) => {
      console.error('Error creating project:', err);
      setError(t('projects.errorCreating'));
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    saveMutation.mutate();
  };
  const loading = saveMutation.isPending;

  return (
    <Container className="py-3">
      <div className="page-header mb-3">
        <Row className="align-items-center">
          <Col>
            <h2 className="page-title">{t('projects.newProject')}</h2>
            <div className="text-secondary small mt-1">{t('projects.requiredFieldsHint')}</div>
          </Col>
        </Row>
      </div>

      <Card>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          <Form onSubmit={handleSubmit}>
            {/* Row 1: Name + Customer */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    {t('projects.projectName')} <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    autoFocus
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('projects.customer')}</Form.Label>
                  <AsyncCreatableSelect
                    name="customer"
                    value={selectedCustomerOption}
                    onChange={(option) => {
                      setSelectedCustomerOption(option);
                      setFormData(prev => ({ ...prev, customer: option ? option.value : '' }));
                    }}
                    onCreateOption={handleCreateCustomer}
                    loadOptions={loadCustomerOptions}
                    defaultOptions
                    cacheOptions
                    placeholder={t('projects.selectCustomer')}
                    isClearable
                    isLoading={creatingCustomer}
                    theme={selectTheme}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    noOptionsMessage={({ inputValue }) => inputValue ? t('common.noOptions') : t('common.startTyping')}
                    loadingMessage={() => t('common.loading')}
                    formatCreateLabel={(input) => `${t('common.create')}: "${input}"`}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Row 2: Location + Status */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('projects.location')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('projects.status')}</Form.Label>
                  <Form.Select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                  >
                    <option value="">{t('projects.statusDefaultOption')}</option>
                    {projectStatuses.map(status => (
                      <option key={status.id} value={status.id}>
                        {status.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {/* Row 3: Start date + End date + Processed by */}
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('projects.startDate')}</Form.Label>
                  <Form.Control
                    type="date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('projects.endDate')}</Form.Label>
                  <Form.Control
                    type="date"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('projects.processedBy')}</Form.Label>
                  <Select
                    name="processed_by"
                    value={crewMemberOptions.find(o => o.value === formData.processed_by) || null}
                    onChange={(selectedOption) => handleSelectChange('processed_by', selectedOption)}
                    options={crewMemberOptions}
                    isClearable
                    isSearchable
                    placeholder={t('projects.selectProcessedBy')}
                    theme={selectTheme}
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Row 4: Currency + Tax rate */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('projects.currency')}</Form.Label>
                  <Select
                    name="currency"
                    value={currencyOptions.find(option => option.value === formData.currency) || null}
                    onChange={(selectedOption) => handleSelectChange('currency', selectedOption)}
                    options={currencyOptions}
                    isClearable
                    isSearchable
                    placeholder={t('projects.selectCurrency')}
                    theme={selectTheme}
                    noOptionsMessage={() => t('projects.noCurrencies')}
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                  <Form.Text className="text-muted">
                    {t('projects.currencyHelp')}
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('projects.taxRate')}</Form.Label>
                  <Form.Control
                    type="number"
                    name="tax_rate"
                    value={formData.tax_rate}
                    onChange={handleChange}
                    step="any"
                    min="0"
                    max="100"
                  />
                  <Form.Text className="text-muted">
                    {t('projects.taxRateHelp')}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {/* Row 5: Budget */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('projects.budget')}</Form.Label>
                  <Form.Control
                    type="number"
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    step="any"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Row 6: Description */}
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('projects.description')}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Row 7: Notes */}
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('projects.notes.title')}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-between mt-4">
              <Button variant="outline-secondary" onClick={() => navigate('/projects')}>
                {t('common.cancel')}
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <InlineLoader className="me-2" />
                    {t('common.saving')}
                  </>
                ) : (
                  t('common.create')
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ProjectCreate;
